<template>
  <div
    v-if="!hideAmounts"
    class="q-mx-xs"
  >
    <h2
      v-if="$register && showTitle"
      class="q-mt-sm"
    >
      {{ $t('pricing.totals') }}
    </h2>

    <q-list
      bordered
      :separator="$register"
      :class="$register ? undefined : 'bg-responsive'"
    >
      <q-item
        v-if="!$register && $q.screen.gt.xs"
        class="bg-responsive-grey-3"
      >
        <q-item-section avatar>
          <q-avatar
            :icon="currentCircuit.iso31661 === 'GB' ? 'fas fa-circle-sterling' : 'fas fa-circle-dollar'"
            text-color="responsive"
          />
        </q-item-section>

        <q-item-section :class="$q.screen.gt.xs ? 'text-h6' : 'text-weight-bolder text-body1'">
          {{ $t('pricing.pricing') }}
        </q-item-section>
      </q-item>
      <q-item
        v-if="order.taxAmount || order.appliedVouchers.length || order.appliedDiscounts.length"
        class="row"
      >
        <q-item-section class="col-grow">
          <q-item-label
            :overline="$register"
            :class="$register ? 'text-uppercase text-weight-bolder' : undefined"
          >
            {{ $t('pricing.subtotal') }}
          </q-item-label>
        </q-item-section>
        <q-item-section class="text-right">
          <i18n-n
            :class="$register ? 'text-weight-bolder' : undefined"
            tag="span"
            :value="displaySubtotal"
            format="currency"
            scope="global"
            data-test-id="payment-subtotal"
          />
        </q-item-section>
      </q-item>
      <q-item
        v-if="!$register && (order.appliedVouchers.length || order.appliedDiscounts.length)"
        class="row"
      >
        <q-item-section class="col-grow">
          <q-item-label
            :overline="$register"
            :class="$register ? 'text-uppercase text-weight-bolder' : 'text-red'"
          >
            {{ $t('pricing.savings') }}
            <q-badge
              v-if="!$register"
              transparent
              dense
              align="middle"
              color="red"
            >
              {{ order.uniqueAppliedVouchers.length + order.appliedDiscounts.length }}  Applied
            </q-badge>
          </q-item-label>
        </q-item-section>
        <q-item-section class="text-right">
          <i18n-n
            :class="$register ? 'text-weight-bolder' : 'text-red'"
            tag="span"
            :value="-(order.appliedVoucherAmount + order.appliedDiscountAmount)"
            format="currency"
            scope="global"
            data-test-id="payment-discount"
          />
        </q-item-section>
      </q-item>
      <q-item
        v-for="bookingFee in bookingFees"
        :key="`bookingFee-${bookingFee.id}`"
        class="row"
      >
        <q-item-section class="col-grow">
          <q-item-label
            :overline="$register"
            :class="$register ? 'text-uppercase text-weight-bolder' : undefined"
          >
            {{ bookingFee.name }}
          </q-item-label>
        </q-item-section>
        <q-item-section class="text-right">
          <i18n-n
            :class="$register ? 'text-weight-bolder' : undefined"
            tag="span"
            :value="bookingFee.subtotal"
            format="currency"
            scope="global"
            data-test-id="payment-bookingfee"
          />
        </q-item-section>
      </q-item>
      <template v-if="currentSite.receiptPrintingOptions.split_taxes && order.taxAmount">
        <q-item
          v-for="taxAmountByTax in order.taxAmountsByTax"
          :key="`taxAmountByTax-${taxAmountByTax.tax_id}`"
          class="row"
        >
          <q-item-section class="col-grow">
            <q-item-label
              :overline="$register"
              :class="$register ? 'text-uppercase text-weight-bolder' : undefined"
            >
              {{ taxAmountByTax.tax_name }}
            </q-item-label>
          </q-item-section>
          <q-item-section class="text-right">
            <i18n-n
              :class="$register ? 'text-weight-bolder' : undefined"
              tag="span"
              :value="taxAmountByTax.tax_amount"
              format="currency"
              scope="global"
              data-test-id="payment-tax"
            />
          </q-item-section>
        </q-item>
      </template>
      <template v-else-if="order.taxAmount">
        <q-item
          class="row"
        >
          <q-item-section class="col-grow">
            <q-item-label
              :overline="$register"
              :class="$register ? 'text-uppercase text-weight-bolder' : undefined"
            >
              {{ $t('pricing.tax') }}
            </q-item-label>
          </q-item-section>
          <q-item-section class="text-right">
            <i18n-n
              :class="$register ? 'text-weight-bolder' : undefined"
              tag="span"
              :value="order.taxAmount"
              format="currency"
              scope="global"
              data-test-id="payment-tax"
            />
          </q-item-section>
        </q-item>
      </template>
      <q-item
        v-if="order.tip"
        class="row"
      >
        <q-item-section class="col-grow">
          <q-item-label
            :overline="$register"
            :class="$register ? 'text-uppercase text-weight-bolder' : undefined"
          >
            {{ $tc('pricing.tip', 1) }}
          </q-item-label>
        </q-item-section>
        <q-item-section class="text-right">
          <i18n-n
            :class="$register ? 'text-weight-bolder' : undefined"
            tag="span"
            class="text-bold"
            :value="order.tip"
            format="currency"
            scope="global"
          />
        </q-item-section>
      </q-item>
      <q-item
        v-if="order.surchargeAmount"
        class="row"
      >
        <q-item-section class="col-grow">
          <q-item-label
            :overline="$register"
            :class="$register ? 'text-uppercase text-weight-bolder' : undefined"
          >
            {{ $t('pricing.surcharge') }}
          </q-item-label>
        </q-item-section>
        <q-item-section class="text-right">
          <i18n-n
            :class="$register ? 'text-weight-bolder' : undefined"
            tag="span"
            :value="order.surchargeAmount"
            format="currency"
            scope="global"
          />
        </q-item-section>
      </q-item>
      <q-item
        class="row"
        :style="{ 'border-top': $register ? undefined : '1px solid rgba(220, 220, 220, 0.5)' }"
      >
        <q-item-section class="col-grow">
          <q-item-label
            :overline="$register"
            :class="$register ? 'text-uppercase' : undefined"
            class="text-weight-bolder"
          >
            Total
          </q-item-label>
        </q-item-section>
        <q-item-section class="text-right">
          <i18n-n
            class="text-weight-bolder"
            tag="span"
            :value="order.total"
            format="currency"
            scope="global"
            data-test-id="payment-total"
          />
        </q-item-section>
      </q-item>
    </q-list>
  </div>
</template>

<script>
import { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'

export default {
  name: 'TotalsSection',
  configuration: ['currentCircuit', 'currentSite', 'discounts'],
  props: {
    order: {
      type: Object,
      required: true
    },
    hideAmounts: {
      type: Boolean,
      required: false,
      default: false
    },
    showTitle: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  computed: {
    displaySubtotal () {
      if (this.$register) {
        return this.order.displaySubtotalPayable
      }

      return this.order.displaySubtotal
    },
    bookingFees () {
      const bookingFees = _(this.order.operativeOrderItems)
        .filter(orderItem => orderItem.type === 'booking-fee' || (_.includes(ORDER_ITEM_TYPES.BOOKING_FEE_CONDITION, orderItem.type) && !!orderItem.localBookingFeeType))
        .groupBy(orderItem => orderItem.localBookingFeeType?.name || orderItem.description)
        .values()
        .sortBy((orderItems) => (_.first(orderItems).bookingFeeType || _.first(orderItems).localBookingFeeType)?.priorityOrder)
        .map((orderItems) => {
          const bookingFeeType = _.first(orderItems).localBookingFeeType || _.first(orderItems).bookingFeeType
          const bookingFeeName = bookingFeeType?.name || _.first(orderItems).description
          const subtotal = _(orderItems).map(orderItem => {
            if (_.includes(ORDER_ITEM_TYPES.BOOKING_FEE_CONDITION, orderItem.type)) {
              return orderItem.bookingFeeAmount
            } else if (orderItem.type === 'booking-fee') {
              return orderItem.subtotal
            }
            return 0
          }).sum()

          return {
            id: bookingFeeType?.id,
            name: bookingFeeName,
            subtotal
          }
        }).value()

      return bookingFees
    }
  }
}
</script>

<style lang="scss" scoped>
h2 {
  color: var(--q-primary);
  font-size: 1.1rem;
  font-weight: bold;
  line-height: 1em;
}
</style>
